import { useEffect, type ReactNode } from "react";
import { formatJPY } from "@shared/money";
import { riskBand, riskBandLabel, type RiskBand } from "@shared/lead-compliance";
import { useStatuses } from "../lib/statuses";

export function Money({ yen }: { yen: number | null | undefined }) {
  return <span className="tabular-nums">{formatJPY(yen)}</span>;
}

export function Spinner() {
  return (
    <div className="flex justify-center py-10">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`card ${className}`}>{children}</div>;
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h1 className="text-lg font-bold text-gray-900 sm:text-xl">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const TONE_CLS: Record<string, string> = {
  gray: "bg-gray-100 text-gray-700",
  green: "bg-brand-100 text-brand-800",
  red: "bg-red-100 text-red-700",
  amber: "bg-amber-100 text-amber-800",
  blue: "bg-blue-100 text-blue-700",
};

// 案件ステータス。組織で定義したラベル・色（status_defs）を反映（未取得時はenumにフォールバック）。
export function StatusBadge({ status }: { status: string }) {
  const { labelOf, toneOf } = useStatuses();
  return <span className={`badge ${TONE_CLS[toneOf("deal", status)] ?? TONE_CLS.gray}`}>{labelOf("deal", status)}</span>;
}

export function Badge({ children, tone = "gray" }: { children: ReactNode; tone?: "gray" | "green" | "red" | "amber" | "blue" }) {
  const map = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-brand-100 text-brand-800",
    red: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-800",
    blue: "bg-blue-100 text-blue-700",
  };
  return <span className={`badge ${map[tone]}`}>{children}</span>;
}

// リスクスコアの見える化（低=緑 / 要確認=黄 / 高・支払不可=赤）
export function RiskBadge({ score, band }: { score?: number | null; band?: RiskBand | null }) {
  if (score == null && !band) return <span className="text-xs text-gray-400">未評価</span>;
  const b: RiskBand = band ?? riskBand(score ?? 0);
  const tone = b === "low" ? "green" : b === "review" ? "amber" : "red";
  return <Badge tone={tone}>{riskBandLabel(b)}{score != null ? `（${score}）` : ""}</Badge>;
}

export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block${className ? ` ${className}` : ""}`}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}

// 保存の結果を画面の下に浮かべて出す（下までスクロールしていても見える）。成功は数秒で消え、失敗は閉じるまで残す。
export function SaveToast({ msg, err, onClose }: { msg?: string; err?: string; onClose: () => void }) {
  useEffect(() => {
    if (!msg || err) return;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [msg, err, onClose]);
  if (!msg && !err) return null;
  return (
    <div role={err ? "alert" : "status"} className={`fixed inset-x-0 bottom-28 z-50 mx-auto flex w-fit max-w-[92vw] items-center gap-3 rounded-2xl px-4 py-2 text-sm text-white shadow-lg lg:bottom-20 ${err ? "bg-red-600" : "bg-brand-600"}`}>
      <span>{err ? `⚠ ${err}` : `✓ ${msg}`}</span>
      {err && <button className="shrink-0 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold" onClick={onClose}>閉じる</button>}
    </div>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{children}</p>;
}
